import * as React from "react";
import { cn } from "@/lib/utils";

type Variant =
  | "h1"
  | "h2"
  | "h3"
  | "h4"
  | "h5"
  | "h6"
  | "subtitle1"
  | "subtitle2"
  | "body1"
  | "body2"
  | "caption"
  | "overline";

const variantStyles: Record<Variant, string> = {
  h1:        "md:text-[2.4rem] text-[22px] font-bold leading-tight",
  h2:        "md:text-[2rem] text-[18px] font-bold leading-tight",
  h3:        "md:text-[1.8rem] text-lg font-semibold leading-snug",
  h4:        "text-[1.5rem] font-semibold leading-snug",
  h5:        "text-lg font-semibold leading-snug",
  h6:        "text-base font-medium leading-snug",
  subtitle1: "text-base font-medium leading-normal",
  subtitle2: "text-sm font-medium leading-normal",
  body1:     "text-base font-normal leading-relaxed",
  body2:     "text-sm font-normal leading-relaxed",
  caption:   "text-xs font-normal leading-normal",
  overline:  "text-xs font-medium uppercase tracking-widest leading-normal",
};

const defaultTag: Record<Variant, keyof React.JSX.IntrinsicElements> = {
  h1:        "h1",
  h2:        "h2",
  h3:        "h3",
  h4:        "h4",
  h5:        "h5",
  h6:        "h6",
  subtitle1: "p",
  subtitle2: "p",
  body1:     "p",
  body2:     "p",
  caption:   "span",
  overline:  "span",
};

const colorStyles: Record<string, string> = {
  default:     "text-foreground",
  muted:       "text-muted-foreground",
  primary:     "text-primary",
  destructive: "text-destructive",
  success:     "text-success",
};

export interface TypographyProps extends React.HTMLAttributes<HTMLElement> {
  variant?: Variant;
  as?: keyof React.JSX.IntrinsicElements;
  color?: "default" | "muted" | "primary" | "destructive" | "success";
}

const Typography = React.forwardRef<HTMLElement, TypographyProps>(
  ({ variant = "body1", as, color = "default", className, children, ...props }, ref) => {
    const Tag = (as ?? defaultTag[variant]) as React.ElementType;
    return (
      <Tag
        ref={ref}
        className={cn(variantStyles[variant], colorStyles[color], className)}
        {...props}
      >
        {children}
      </Tag>
    );
  }
);

Typography.displayName = "Typography";

export { Typography };
